/**
 * Olfactum — Quiz Olfativo v2
 * Alpine.js component: Alpine.data('quizApp')
 *
 * Flujo: intro → preguntas (1-8) → análisis → resultados personalizados
 * Scoring: vector ponderado por dimensión (familia/proyección/carácter)
 *   con normalización por raíz del número de tags, veto duro de exclusiones,
 *   filtro de budget, diversidad de familia en top 3, y boosts por texto libre.
 */

const DIMENSION_WEIGHTS = {
  floral: 3, amaderado: 3, oriental: 3, fresco: 3, citrico: 3,
  especiado: 3, cuero: 3, dulce: 3, arabe: 3,
  sutil: 2, moderado: 2, intenso: 2,
  casual: 2, formal: 2, seductor: 2, versatil: 2,
  discreto: 2, presencia: 2, memorable: 2
};

const FAMILY_DIMENSIONS = [
  'floral', 'amaderado', 'oriental', 'fresco', 'citrico',
  'especiado', 'cuero', 'dulce', 'arabe'
];

const BUDGET_CAPS = {
  'budget-low': 30000,
  'budget-mid': 50000,
  'budget-high': 100000,
  'budget-premium': Infinity
};

const TAG_LABELS = {
  floral: 'notas florales', amaderado: 'maderas c\u00e1lidas',
  oriental: 'notas orientales', fresco: 'frescura',
  citrico: 'c\u00edtricos', arabe: 'perfumer\u00eda \u00e1rabe',
  especiado: 'especias', cuero: 'cuero', dulce: 'dulzura',
  sutil: 'sutileza', intenso: 'presencia intensa',
  moderado: 'presencia equilibrada', seductor: 'car\u00e1cter seductor',
  casual: 'versatilidad diaria', formal: 'elegancia formal',
  discreto: 'discreci\u00f3n', versatil: 'versatilidad',
  presencia: 'proyecci\u00f3n notable', memorable: 'permanencia memorable'
};

const OPEN_KEYWORD_BOOSTS = [
  { keywords: ['verano', 'calor', 'playa'], boost: { fresco: 1, citrico: 1 } },
  { keywords: ['invierno', 'frio', 'frío', 'noche'], boost: { amaderado: 1, oriental: 1, intenso: 1 } },
  { keywords: ['oficina', 'trabajo', 'diario'], boost: { casual: 1, sutil: 1, versatil: 1 } },
  { keywords: ['cita', 'fiesta', 'especial'], boost: { seductor: 1, intenso: 1, memorable: 1 } },
  { keywords: ['dulce'], boost: { dulce: 1 } },
  { keywords: ['madera'], boost: { amaderado: 1 } },
  { keywords: ['flor'], boost: { floral: 1 } },
  { keywords: ['fresco'], boost: { fresco: 1 } },
  { keywords: ['fuerte'], boost: { intenso: 1 } }
];

function parseAnswerTags(answers) {
  const tags = [];
  answers.forEach((val) => {
    if (!val || typeof val !== 'string' || val.startsWith('_')) return;
    val.split(',').map(t => t.trim().toLowerCase()).filter(Boolean).forEach(t => tags.push(t));
  });
  return tags;
}

function buildUserVector(answerTags, openAnswer) {
  const vector = {};
  const exclusions = [];
  let budgetTag = null;

  answerTags.forEach((tag) => {
    if (tag.startsWith('-')) {
      exclusions.push(tag.substring(1));
      return;
    }
    if (tag.startsWith('budget-')) {
      budgetTag = tag;
      return;
    }
    const weight = DIMENSION_WEIGHTS[tag];
    if (weight) vector[tag] = (vector[tag] || 0) + weight;
  });

  const openBoost = boostsFromOpenAnswer(openAnswer);
  Object.entries(openBoost).forEach(([dim, w]) => {
    vector[dim] = (vector[dim] || 0) + w;
  });

  return { vector, exclusions, budgetTag };
}

function boostsFromOpenAnswer(openAnswer) {
  const boost = {};
  if (!openAnswer || typeof openAnswer !== 'string') return boost;
  const normalized = openAnswer.toLowerCase();
  OPEN_KEYWORD_BOOSTS.forEach(({ keywords, boost: delta }) => {
    const hit = keywords.some(k => normalized.includes(k));
    if (!hit) return;
    Object.entries(delta).forEach(([dim, w]) => {
      boost[dim] = (boost[dim] || 0) + w;
    });
  });
  return boost;
}

function productVector(product) {
  const raw = product && product.quiz_profile;
  // Shopify metafield tipo JSON llega como objeto; string es otro caso válido.
  let parsed = null;
  if (raw && typeof raw === 'object') {
    parsed = raw;
  } else if (raw && typeof raw === 'string' && raw.length > 0) {
    try { parsed = JSON.parse(raw); } catch (_err) { parsed = null; }
  }
  if (parsed && typeof parsed === 'object') {
    const cleaned = {};
    // Soporta estructura plana {"amaderado":3} y anidada {"familia":{"amaderado":3}}
    const absorb = (obj) => {
      Object.entries(obj).forEach(([k, v]) => {
        if (v && typeof v === 'object' && !Array.isArray(v)) {
          absorb(v);
          return;
        }
        const num = Number(v);
        if (Number.isFinite(num) && num > 0) cleaned[k.toLowerCase()] = num;
      });
    };
    absorb(parsed);
    if (Object.keys(cleaned).length > 0) return cleaned;
  }
  const vec = {};
  if (Array.isArray(product.tags)) {
    product.tags.forEach((tag) => {
      const t = String(tag).toLowerCase();
      if (DIMENSION_WEIGHTS[t]) vec[t] = (vec[t] || 0) + 1;
    });
  }
  return vec;
}

function dotScore(userVec, productVec) {
  let score = 0;
  Object.entries(userVec).forEach(([dim, uw]) => {
    const pw = productVec[dim];
    if (pw) score += uw * pw;
  });
  return score;
}

function normalize(score, productVec) {
  // Normalizamos por sqrt(#dim presentes) para que productos sobre-tagueados
  // no ganen solo por volumen; sqrt mantiene la curva suave vs dividir por N.
  const count = Math.max(1, Object.keys(productVec).length);
  return score / Math.sqrt(count);
}

function hasExcludedTag(product, exclusions) {
  if (exclusions.length === 0) return false;
  const tags = (product.tags || []).map(t => String(t).toLowerCase());
  return exclusions.some(ex => tags.includes(ex));
}

function dominantFamily(product) {
  const vec = productVector(product);
  let best = null;
  let bestW = 0;
  FAMILY_DIMENSIONS.forEach((fam) => {
    const w = vec[fam] || 0;
    if (w > bestW) { bestW = w; best = fam; }
  });
  return best;
}

function applyDiversity(ranked) {
  if (ranked.length < 3) return ranked;
  const top3 = ranked.slice(0, 3);
  const fams = top3.map(r => r._family);
  if (fams[0] && fams[0] === fams[1] && fams[1] === fams[2]) {
    const replacement = ranked.slice(3).find(r => r._family && r._family !== fams[0] && r.score > 0);
    if (replacement) {
      return [top3[0], top3[1], replacement, ...ranked.slice(3).filter(r => r !== replacement)];
    }
  }
  return ranked;
}

function topDimensions(vector, n) {
  return Object.entries(vector)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([dim]) => dim);
}

document.addEventListener('alpine:init', () => {
  Alpine.data('quizApp', (config) => ({
    // ── Estado ──
    screen: 'intro',
    currentStep: 0,
    answers: [],
    openAnswer: '',
    results: [],
    loading: false,
    _stepChanging: false,
    _profileDescription: '',
    _userProfile: {},

    questions: config?.questions || [],
    resultProducts: config?.resultProducts || [],

    get totalSteps() {
      return this.questions.length;
    },

    get currentQuestion() {
      if (this._stepChanging) return null;
      return this.questions[this.currentStep] || null;
    },

    get progressPercent() {
      if (this.totalSteps === 0) return 0;
      return Math.round(((this.currentStep + 1) / this.totalSteps) * 100);
    },

    start() {
      this.screen = 'question';
      this.currentStep = 0;
      this.answers = [];
      this.openAnswer = '';
      this._userProfile = {};
    },

    restart() {
      this.screen = 'intro';
      this.currentStep = 0;
      this.answers = [];
      this.openAnswer = '';
      this.results = [];
      this._stepChanging = false;
      this._profileDescription = '';
      this._userProfile = {};
    },

    goBack() {
      if (this._stepChanging) return;
      if (this.currentStep > 0) {
        this._stepChanging = true;
        this.$nextTick(() => {
          this.currentStep--;
          this.$nextTick(() => { this._stepChanging = false; });
        });
      } else {
        this.screen = 'intro';
      }
    },

    answer(optionValue) {
      const nextAnswers = this.answers.slice();
      nextAnswers[this.currentStep] = optionValue;
      this.answers = nextAnswers;
      if (this.currentStep < this.totalSteps - 1) {
        const next = this.currentStep + 1;
        this._stepChanging = true;
        this.$nextTick(() => {
          this.currentStep = next;
          this.$nextTick(() => { this._stepChanging = false; });
        });
      } else {
        this.computeResults();
      }
    },

    submitOpen() {
      const nextAnswers = this.answers.slice();
      nextAnswers[this.currentStep] = '_open';
      this.answers = nextAnswers;
      this.computeResults();
    },

    skipOpen() {
      const nextAnswers = this.answers.slice();
      nextAnswers[this.currentStep] = '_skip';
      this.answers = nextAnswers;
      this.openAnswer = '';
      this.computeResults();
    },

    computeResults() {
      this.loading = true;
      this.screen = 'results';

      this.$nextTick(() => {
        setTimeout(() => {
          const answerTags = parseAnswerTags(this.answers);
          const { vector: userVec, exclusions, budgetTag } = buildUserVector(answerTags, this.openAnswer);
          const priceMax = budgetTag ? (BUDGET_CAPS[budgetTag] ?? Infinity) : Infinity;

          const ranked = this.resultProducts
            // Veto duro: una exclusión explícita del usuario elimina al candidato
            // en vez de solo restar score; intención del usuario > coincidencias.
            .filter(p => !hasExcludedTag(p, exclusions))
            .map((product) => {
              const pVec = productVector(product);
              const raw = dotScore(userVec, pVec);
              let score = normalize(raw, pVec);
              if (product.price > priceMax) score -= 2;
              return { product, score, _family: dominantFamily(product), _pVec: pVec };
            })
            .sort((a, b) => b.score - a.score);

          const diversified = applyDiversity(ranked);
          const topTags = topDimensions(userVec, 3);

          this._userProfile = { ...userVec };
          this._profileDescription = topTags.map(t => TAG_LABELS[t] || t).join(', ');

          const labels = [
            'Tu Match Principal',
            'Tu Segunda Opci\u00f3n',
            'Tambi\u00e9n Te Encantar\u00e1'
          ];

          this.results = diversified.slice(0, 3)
            .filter(r => r && r.product)
            .map((entry, idx) => {
              const shared = Object.keys(entry._pVec)
                .filter(dim => userVec[dim])
                .sort((a, b) => (userVec[b] * entry._pVec[b]) - (userVec[a] * entry._pVec[a]))
                .slice(0, 2)
                .map(dim => TAG_LABELS[dim] || dim);
              const explanation = shared.length > 0
                ? 'Coincide con tu preferencia por ' + shared.join(' y ') + '.'
                : 'Una fragancia que complementa tu perfil olfativo.';
              return { label: labels[idx], product: entry.product, explanation };
            });

          this.loading = false;
        }, 800);
      });
    },

    formatPrice(cents) {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency', currency: 'MXN',
        minimumFractionDigits: 0, maximumFractionDigits: 0
      }).format(cents / 100);
    },

    async addResultToCart(variantId) {
      if (!variantId) return;
      await Alpine.store('cart').addItem(variantId, 1);
    }
  }));
});
